import {
    type FC,
    useMemo,
    useState,
    type ReactNode,
    useEffect,
    useContext,
    useCallback,
    createContext,
} from 'react';
import serverVars from 'server-vars';
import { useFragment, graphql } from 'react-relay';
import { getBuyerId, getGuestId, getUserType } from 'dibs-cookie-jar';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants.ts';
import {
    addLoginHandlers,
    sendChatMessageOnPageRefetch,
} from 'dibs-buyer-layout/exports/loginHandlers.jsx';
import { useCurrency } from '../../../shared/hooks/useCurrency.ts';
import {
    getUserZipCode,
    getUserSessionRegions,
    getUserSessionCountryCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import {
    SOLD_PDP_YMAL_SL_SIZE,
    SOLD_PDP_YMAL_SL_INITIAL,
} from '../../PdpRespSimilarItems/pdpRespSimilarItemsConstants.ts';

// types
import { type PdpRespUnavailableContextProvider_item$key as Item } from './__generated__/PdpRespUnavailableContextProvider_item.graphql.ts';
import { type PdpRespUnavailableLayoutEntryQuery$variables as RelayVars } from '../__generated__/PdpRespUnavailableLayoutEntryQuery.graphql.ts';

type Refetch = (vars: $TSFixMe, options: { onComplete: (error: Error | null) => void }) => void;

type PdpRespUnavailableContextType = {
    refetch: Refetch;
    currency: string;
    relayVars: RelayVars;
};

const relayDefaults = {
    userId: '',
    guestId: '',
    itemId: '',
    itemIds: [''],
    isTrade: false,
    fetchPdp: false,
    fetchChat: false,
    fetchUser: false,
    afterDisplay: false,
    afterDisplayOrBtSsr: false,
    afterDisplayAndBtNone: false,
    fetchRegionalInfo: false,
};

const PdpRespUnavailableContext = createContext<PdpRespUnavailableContextType>({
    currency: '',
    refetch: () => {},
    relayVars: relayDefaults,
});

const itemFragment = graphql`
    fragment PdpRespUnavailableContextProvider_item on Item {
        serviceId
        seller {
            isAccessible
        }
    }
`;

export const PdpRespUnavailableContextProvider: FC<{
    item: Item;
    refetch: Refetch;
    children: ReactNode;
}> = ({ refetch, item: itemRef, children }) => {
    const item = useFragment(itemFragment, itemRef);
    const { currency } = useCurrency();
    const { isBot, soldPdpSponsoredYmalGridTest } = useServerVarsContext();
    const isInSoldPdpSponsoredYmalGridTest = !!soldPdpSponsoredYmalGridTest;
    const isSoldPdpSponsoredYmalGridVariant = soldPdpSponsoredYmalGridTest === 'variant';

    const [relayVars, setRelayVars] = useState({
        ...relayDefaults,
        itemId: item?.serviceId || '',
        itemIds: [item?.serviceId || ''],
    });
    const isSellerAccessible = !!item?.seller?.isAccessible;

    const handleRefetch = useCallback(() => {
        const userId = getBuyerId(document.cookie) || '';
        const guestId = getGuestId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const isTrade = userType === CUSTOMER_TYPE_TRADE;

        // Grid AB (both arms): pageSize = Topsort/eligibleItemIds pool; maxItems = inflate cap.
        // Control: carousel only; variant: grid (up to 40) + optional carousel leftovers.
        // When the AB is off, omit these so the query defaults apply.
        const sponsoredFetchVars = isInSoldPdpSponsoredYmalGridTest
            ? {
                  numOfSponsoredItems: SOLD_PDP_YMAL_SL_SIZE,
                  maxSponsoredItems: SOLD_PDP_YMAL_SL_INITIAL,
                  minSponsoredItemsSteps: [5, 10] as number[],
              }
            : {};

        const updatedRelayVars = {
            userId,
            guestId,
            isTrade,
            fetchPdp: isTrade,
            afterDisplay: true,
            afterDisplayOrBtSsr: true,
            afterDisplayAndBtNone: !isBot,
            fetchUser: !!userId,
            fetchChat: isSellerAccessible,
            userZipCode: getUserZipCode(),
            fetchRegionalInfo: !getUserSessionRegions(),
            userCountryCode: getUserSessionCountryCode(),
            isSoldPdpSponsoredYmalGridVariant,
            ...sponsoredFetchVars,
        };
        refetch(updatedRelayVars, {
            onComplete: error => {
                if (!error) {
                    setRelayVars(() => ({
                        ...serverVars.get('relayVars'),
                        ...updatedRelayVars,
                    }));
                    sendChatMessageOnPageRefetch();
                }
            },
        });
    }, [
        refetch,
        isBot,
        isSellerAccessible,
        isInSoldPdpSponsoredYmalGridTest,
        isSoldPdpSponsoredYmalGridVariant,
    ]);

    //After refetching the item will change and this will trigger the refetch again
    //Firing the useEffect only once will prevent unnecessary refetches
    useEffect(() => {
        handleRefetch();
        addLoginHandlers(() => {
            handleRefetch();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            refetch,
            currency,
            relayVars,
        }),
        [refetch, currency, relayVars]
    );

    return <PdpRespUnavailableContext value={value}>{children}</PdpRespUnavailableContext>;
};

export const useRespUnavailableContext = (): PdpRespUnavailableContextType => {
    return useContext(PdpRespUnavailableContext);
};
